/**
 * Multi-firm management — Enterprise master dashboard.
 *
 * Lists every child firm under the parent admin's account with roll-up stats,
 * a coloured health indicator, and a one-click "Switch firm" action that sets
 * the active-firm-id cookie server-side and reloads into the child context.
 */
import React, { useEffect, useState } from "react";
import { Plus, ArrowRightLeft, Trash2, Building2, Loader2, X, AlertTriangle, CheckCircle2, Activity, Users, ShieldQuestion, CalendarCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";

const HEALTH_COLOUR = {
  green: { bg: "bg-emerald2/10", text: "text-emerald2", dot: "bg-emerald2", label: "Healthy" },
  amber: { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500", label: "Needs attention" },
  red:   { bg: "bg-rose-100",   text: "text-rose-700",  dot: "bg-rose-500",  label: "Action required" },
};

export default function MultiFirm() {
  const [data, setData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", fca_number: "", firm_type: "ifa", firm_size: "small" });
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/api/multi-firm");
      setData(data);
    } catch (e) {
      setError(formatApiError(e));
      setData({ firms: [], totals: {} });
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (form.name.length < 2 || form.fca_number.length < 4) return;
    setBusy("create");
    try {
      await api.post("/api/multi-firm", form);
      setShowAdd(false); setForm({ name: "", fca_number: "", firm_type: "ifa", firm_size: "small" });
      await load();
    } catch (e) { alert(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const switchTo = async (firmId, firmName) => {
    setBusy("switch-" + firmId);
    try {
      await api.post(`/api/multi-firm/switch/${firmId}`);
      window.location.href = "/app/dashboard"; // reload into child context
    } catch (e) { alert(formatApiError(e)); setBusy(null); }
  };

  const revoke = async (firmId, firmName) => {
    if (!confirm(`Revoke access for "${firmName}"? Their data is preserved but they can no longer be accessed.`)) return;
    setBusy("revoke-" + firmId);
    try { await api.delete(`/api/multi-firm/${firmId}`); await load(); }
    catch (e) { alert(formatApiError(e)); }
    finally { setBusy(null); }
  };

  if (!data) return <div className="card2 p-8 text-center"><Loader2 className="animate-spin mx-auto text-slate2" /></div>;
  if (error) return <div className="card2 p-8 text-center text-slate2" data-testid="multi-firm-error">{error}</div>;

  const totals = data.totals || {};

  return (
    <div className="space-y-6" data-testid="multi-firm-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="t-h1">Master dashboard</h1>
          <p className="t-body-sm text-slate2 mt-1">Manage every firm in your network from one place.</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn btn-primary" data-testid="add-firm">
          <Plus size={16}/>Add new firm
        </button>
      </div>

      {/* Roll-up stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total clients", value: totals.clients ?? 0, icon: Users },
          { label: "Scans this month", value: totals.scans ?? 0, icon: Activity },
          { label: "AML flags pending", value: totals.aml_pending ?? 0, icon: ShieldQuestion },
          { label: "Reviews overdue", value: totals.reviews_overdue ?? 0, icon: CalendarCheck },
        ].map((s) => (
          <div key={s.label} className="card2 p-4">
            <div className="flex justify-between items-start">
              <div>
                <div className="t-label text-slate2">{s.label}</div>
                <div className="t-h2 mt-1">{s.value}</div>
              </div>
              <s.icon size={18} className="text-slate2 mt-1" />
            </div>
          </div>
        ))}
      </div>

      {/* Firms grid */}
      {data.firms.length === 0 ? (
        <div className="card2 p-12 text-center">
          <Building2 size={28} className="text-slate2 mx-auto mb-3" />
          <h3 className="t-h3 mb-1">No child firms yet</h3>
          <p className="t-body-sm text-slate2 mb-4">Create your first firm to begin managing it from this dashboard.</p>
          <button onClick={() => setShowAdd(true)} className="btn btn-primary"><Plus size={16}/>Add a firm</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.firms.map((f) => {
            const h = HEALTH_COLOUR[f.health] || HEALTH_COLOUR.green;
            return (
              <div key={f.id} className="card2 p-5 flex flex-col" data-testid={`firm-card-${f.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {f.logo ? <img src={f.logo} alt="" className="w-9 h-9 rounded-lg object-cover" /> : <div className="w-9 h-9 rounded-lg bg-indigo2/10 flex items-center justify-center"><Building2 size={16} className="text-indigo2"/></div>}
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{f.name}</div>
                      <span className={`inline-flex items-center gap-1 ${h.bg} ${h.text} text-xs px-2 py-0.5 rounded-full mt-1`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${h.dot}`}/> {h.label}
                      </span>
                    </div>
                  </div>
                  {f.status === "revoked" && <span className="badge2 badge-warning">Revoked</span>}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm mb-4 mt-1">
                  <div><div className="t-label text-slate2">Clients</div><div className="font-mono">{f.clients}</div></div>
                  <div><div className="t-label text-slate2">Scans / mo</div><div className="font-mono">{f.scans_this_month}</div></div>
                  <div><div className="t-label text-slate2">AML pending</div><div className={`font-mono ${f.aml_pending > 0 ? "text-amber-600" : ""}`}>{f.aml_pending}</div></div>
                  <div><div className="t-label text-slate2">Reviews overdue</div><div className={`font-mono ${f.reviews_overdue > 0 ? "text-rose-600" : ""}`}>{f.reviews_overdue}</div></div>
                </div>
                <div className="t-caption mb-3">Last activity: {f.last_activity ? new Date(f.last_activity).toLocaleString() : "—"}</div>
                <div className="mt-auto flex gap-2">
                  <button onClick={() => switchTo(f.id, f.name)} disabled={busy === "switch-" + f.id || f.status === "revoked"} className="btn btn-primary btn-sm flex-1" data-testid={`switch-${f.id}`}>
                    {busy === "switch-" + f.id ? <Loader2 size={14} className="animate-spin"/> : <ArrowRightLeft size={14}/>}Switch
                  </button>
                  <button onClick={() => revoke(f.id, f.name)} disabled={busy === "revoke-" + f.id || f.status === "revoked"} className="btn btn-ghost btn-sm text-rose-600" data-testid={`revoke-${f.id}`}>
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="card2 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="t-h3">Add a firm</h3>
              <button onClick={() => setShowAdd(false)}><X size={18}/></button>
            </div>
            <div className="space-y-3">
              <input className="input2 w-full" placeholder="Firm name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="firm-name" />
              <input className="input2 w-full" placeholder="FCA number" value={form.fca_number} onChange={(e)=>setForm({...form, fca_number:e.target.value})} data-testid="firm-fca" />
              <select className="input2 w-full" value={form.firm_type} onChange={(e)=>setForm({...form, firm_type:e.target.value})}>
                <option value="ifa">IFA</option><option value="mortgage_broker">Mortgage broker</option>
                <option value="insurance_broker">Insurance broker</option><option value="fintech">Fintech</option>
                <option value="consumer_credit">Consumer credit</option><option value="other">Other</option>
              </select>
              <select className="input2 w-full" value={form.firm_size} onChange={(e)=>setForm({...form, firm_size:e.target.value})}>
                <option value="sole">Sole practitioner</option><option value="small">2-10 staff</option>
                <option value="medium">11-50 staff</option><option value="large">50+ staff</option>
              </select>
              <button onClick={create} disabled={busy === "create"} className="btn btn-primary w-full" data-testid="firm-create">
                {busy === "create" ? <Loader2 size={14} className="animate-spin"/> : "Create firm"}
              </button>
              <p className="t-caption text-center">Child firms inherit your Enterprise plan — no extra charges.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
